'use client';

import { Provider } from 'react-redux';
import { Slide, ToastContainer } from 'react-toastify';
import ErrorBoundary from 'src/components/Layout/ErrorBoundary';
import FallbackPage from 'src/components/Layout/ErrorBoundary/FallbackPage';
import MaintenanceGate from 'src/components/Layout/MaintenanceMode';
import Modals from 'src/components/Modals';
import type { LaunchDarklyBootstrap } from 'src/config/LDFeatureFlags';
import { ProvideAuth } from 'src/context/AuthContext';
import { HeaderTextProvider } from 'src/context/HeaderContext';
import { SidebarToggleProvider } from 'src/context/SidebarToggleContext';
import { store } from 'src/redux/store';
import type { ClientAuthBootstrap } from 'src/shared/auth/clientAuthBootstrap';
import { DatadogInit } from './datadog-init';
import FullStoryInit from './fullstory-init';
import { LaunchDarklyProvider } from './LaunchDarklyProvider';

export function Providers({
  children,
  // APX-479 (AC-3 clause 2): the `(public)` group (onboarding, owner quote
  // approval) is entered logged-out and provably consumes NO LaunchDarkly:
  // nothing in that group's static import closure calls `useFlags`/`useLDClient`
  // (verified), and `state.profile.ldIdentified` is read only by `(coordinator)`
  // surfaces — the route-group layouts plus components rendered beneath them
  // (`useSidebarSettings`, the quote action menu). None are reachable from
  // `(public)`. So the heavy LaunchDarkly client SDK — which opens a live streaming
  // connection and pulls the flag set — is pure waste on public pages. This prop
  // lets the public island omit it. It defaults to `true`, so every other caller
  // (root OFF-path, `(auth)`, `(coordinator)`, `(vendor-portal)`, `style-guide`)
  // renders the EXACT same provider tree as before — behaviour is unchanged
  // there.
  //
  // NOTE — aws-amplify / `ProvideAuth` is intentionally NOT made optional here:
  // the `/onboarding` public page authenticates the vendor via Amplify
  // (`useAuth().signIn` / `checkAuth` / `signOutAndClearOnly`, see
  // src/views/VendorPortal/Onboarding/index.tsx), so AuthContext must stay in the
  // public island. Removing it would break onboarding — the exact class of
  // correctness regression this split must avoid.
  includeLaunchDarkly = true,
  // APX-672. FullStory captures 100% of sessions with no sampling and no privacy
  // configuration, and — unlike Datadog RUM — it has NO code-side masking lever:
  // its global fail-closed default is an org-console setting with no counterpart
  // in `SnippetOptions` (@fullstory/browser/dist/index.d.ts). So on a surface we
  // decide should not be recorded, not mounting the initializer is the only
  // control this repo owns.
  //
  // Set `false` by `(public)`, whose subjects are property owners reaching a
  // signed quote link with no account, no login and no notice — the one audience
  // that is recorded without ever being party to the session. Every authenticated
  // group keeps FullStory; there the users are staff or contracted vendors, and
  // Datadog's fail-closed level covers the rendered PII.
  //
  // NOTE this does NOT gate `<DatadogInit />`. `(public)/quotes` is a revenue path
  // and its error + INP telemetry is worth keeping, now that replay content is
  // masked by default.
  includeSessionCapture = true,
  // COM-858: server-resolved Cognito fields so Amplify configures at hydration. Only the
  // `(coordinator)` group passes it; everywhere else it's absent and AuthContext fetches the
  // config itself, exactly as before.
  initialAuthConfig = null,
  // Server-evaluated flags for the session org; only the `(coordinator)` group passes it.
  launchDarklyBootstrap = null,
}: {
  children: React.ReactNode;
  includeLaunchDarkly?: boolean;
  includeSessionCapture?: boolean;
  initialAuthConfig?: ClientAuthBootstrap | null;
  launchDarklyBootstrap?: LaunchDarklyBootstrap | null;
}) {
  // The Redux `Provider` is the single hard dependency of everything below
  // (AuthContext, LaunchDarkly's identifier, Modals all read the store), so it
  // always wraps the inner graph. LaunchDarkly, when included, sits between Redux
  // and AuthContext exactly as before — omitting it just removes that one layer,
  // leaving the nesting of every other provider byte-for-byte identical.
  const providerGraph = (
    <ProvideAuth initialAuthConfig={initialAuthConfig}>
      <SidebarToggleProvider>
        <HeaderTextProvider>
          <DatadogInit />
          {includeSessionCapture && <FullStoryInit />}
          {children}
          <Modals />
          {/* App-wide backend maintenance-mode window. Mounted here so a
              `php artisan down` detected on ANY route group — coordinator,
              vendor portal, auth, public — blacks out the whole app and polls
              for recovery. Renders nothing until maintenance is detected. */}
          <MaintenanceGate />
          <ToastContainer
            position="bottom-right"
            autoClose={3000}
            hideProgressBar
            newestOnTop={false}
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            closeButton={false}
            closeOnClick={false}
            className="ls-toastify"
            toastClassName="ls-toastify-inner"
            transition={Slide}
          />
        </HeaderTextProvider>
      </SidebarToggleProvider>
    </ProvideAuth>
  );

  return (
    <ErrorBoundary fallback={<FallbackPage hideGoHome />}>
      <Provider store={store}>
        {includeLaunchDarkly ? (
          <LaunchDarklyProvider bootstrap={launchDarklyBootstrap}>
            {providerGraph}
          </LaunchDarklyProvider>
        ) : (
          providerGraph
        )}
      </Provider>
    </ErrorBoundary>
  );
}
